import PaymentAcceptanceChoice from "../components/PaymentAcceptanceChoice";
import React, { useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { isAuthenticated } from "../../auth";
import { getUserProductPreview } from "../../auth/apiProduct";
import { resyncSellerProviderLink, changeSellerPaymentAcceptanceMode } from "../services/providerProductService";
import FulfillmentChanger from "../ui/FulfillmentChanger";
import SyncReport from "../components/SyncReport";
import ProductStatusSwitch from "../../pages/editProduct/ProductStatusSwitch";
import { elakayAutoText } from "../../pageInside/dataElakay";
import "./ResyncModal.css";

const MODE_KEY = { PROVIDER_DROPSHIPPING: "modeDs", PROVIDER_STOCK_SELLER_OWNED: "modeStore", SELLER_STOCK: "modeShip" };

// Resynchronize modal (ticket §6-§21): the Seller chooses Automatic (refresh Provider-controlled
// data only, keep every Seller setting -- exactly today's Resync) or Manual (same mandatory
// Provider refresh, THEN review/edit fulfillment + pricing on the SAME Seller Product/link -- never
// a new Product, never a new link). Manual's pricing step embeds ProductStatusSwitch as-is: it
// already IS the Sales Simulation engine wired to this exact Product (server_elakay's
// buildProfitReport, the same function the standalone Sales Simulation tool calls), already
// prefills the Seller's saved pricingMode/Desired Profit/Target Selling Price from MongoDB, already
// re-reads the live Provider Price every time, and already saves/activates through the real,
// backend-authoritative endpoints -- reusing it here means Manual Synchronization can never drift
// into a second, competing pricing formula (ticket's CORE RULE).
export default function ResyncModal({ show, onHide, link, ct, onSynced }) {
  const [method, setMethod] = useState("automatic");
  const [phase, setPhase] = useState("choose"); // choose | working | manual | done
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [product, setProduct] = useState(null);
  const [acceptanceMode, setAcceptanceMode] = useState("seller");
  const [paymentRules, setPaymentRules] = useState(null);
  const [paymentNeedsConfirmation, setPaymentNeedsConfirmation] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const paymentSavingRef = useRef(false);
  const syncingRef = useRef(false);

  useEffect(() => {
    if (!show) return;
    setMethod("automatic"); setPhase("choose"); setResult(null); setErrorMsg(""); setProduct(null);
    syncingRef.current = false;
    setSyncing(false);
    setAcceptanceMode(link.paymentAcceptanceNeedsConfirmation ? "seller" : (link.acceptanceMode || "seller"));
    setPaymentRules(link.providerProductId?.dropshippingPayment || null);
    setPaymentNeedsConfirmation(Boolean(link.paymentAcceptanceNeedsConfirmation));
  }, [show, link?._id]);

  if (!link) return null;
  const provider = link.providerProductId;
  const token = isAuthenticated()?.token;

  const runResync = async () => {
    const r = await resyncSellerProviderLink(token, link._id);
    if (r?.error) { setErrorMsg(ct("resyncFail")); setPhase("choose"); return null; }
    setResult(r);
    setAcceptanceMode(r.paymentAcceptance?.needsConfirmation ? "seller" : (r.link?.acceptanceMode || link.acceptanceMode || "seller"));
    setPaymentRules(r.providerProduct?.dropshippingPayment || provider?.dropshippingPayment || null);
    setPaymentNeedsConfirmation(Boolean(r.paymentAcceptance?.needsConfirmation || r.link?.paymentAcceptanceNeedsConfirmation));
    onSynced?.(link._id, r);
    return r;
  };

  const saveAcceptanceMode = async (nextMode) => {
    const rules = paymentRules || provider?.dropshippingPayment || {};
    if (paymentSavingRef.current || (nextMode === "provider" && !rules.sellerPaymentAcceptanceAllowed)) return;
    paymentSavingRef.current = true;
    setPaymentSaving(true); setErrorMsg("");
    try {
      const r = await changeSellerPaymentAcceptanceMode(token, link._id, nextMode);
      if (r?.error) throw new Error(r.error);
      setAcceptanceMode(r.acceptanceMode || nextMode);
      setPaymentRules(r.paymentAcceptance || rules);
      setPaymentNeedsConfirmation(false);
      onSynced?.(link._id);
    } catch (error) { setErrorMsg(error.message); }
    finally { paymentSavingRef.current = false; setPaymentSaving(false); }
  };

  const handleContinue = async () => {
    // React state updates are batched. The ref closes the small gap where two
    // rapid clicks could otherwise start two POSTs before `phase` becomes
    // "working". A resync is an update of the existing link, so duplicate
    // requests here are especially undesirable.
    if (syncingRef.current) return;
    syncingRef.current = true;
    setSyncing(true);
    setPhase("working"); setErrorMsg("");
    try {
      const synced = await runResync();
      if (!synced) return;
      if (method === "automatic") { setPhase("done"); return; }
      // Manual: reload the Seller's OWN product fresh (Source B, ticket §12) -- the pricing/fulfillment
      // step below always reads it live, never a stale copy carried from before the modal opened.
      const productData = await getUserProductPreview(link.sellerProductId);
      if (productData?.error) throw new Error(productData.error);
      if (!productData?.product) throw new Error("The linked Seller product could not be loaded.");
      setProduct(productData.product);
      setPhase("manual");
    } catch (error) {
      console.error("Product resynchronization failed:", error);
      setErrorMsg(error?.message || ct("resyncFail"));
      setPhase("choose");
    } finally {
      syncingRef.current = false;
      setSyncing(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size={phase === "manual" ? "lg" : undefined} scrollable className="resync-modal" data-testid="resync-modal">
      <Modal.Header closeButton>
        <Modal.Title>{elakayAutoText("h_resync_title", "Resynchronize Product")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="resync-modal__product">{provider?.name}</p>
        {provider?.shop?.shopName && (
          <p className="resync-modal__provider">{elakayAutoText("h_resync_provider", "Provider")}: {provider.shop.shopName}</p>
        )}

        {phase === "choose" && (
          <fieldset className="resync-modal__choice" role="radiogroup" aria-label={elakayAutoText("h_resync_method", "Choose synchronization method")}>
            <legend>{elakayAutoText("h_resync_method", "Choose synchronization method")}</legend>
            <label className={`resync-modal__option${method === "automatic" ? " is-selected" : ""}`}>
              <input type="radio" name="resyncMethod" checked={method === "automatic"} onChange={() => setMethod("automatic")} />
              <span>
                <strong>{elakayAutoText("h_resync_auto", "Automatic Synchronization")}</strong>
                <small>{elakayAutoText("h_resync_auto_hint", "Refresh the latest provider data. Your price and settings stay exactly as they are.")}</small>
              </span>
            </label>
            <label className={`resync-modal__option${method === "manual" ? " is-selected" : ""}`}>
              <input type="radio" name="resyncMethod" checked={method === "manual"} onChange={() => setMethod("manual")} />
              <span>
                <strong>{elakayAutoText("h_resync_manual", "Manual Synchronization")}</strong>
                <small>{elakayAutoText("h_resync_manual_hint", "Refresh the latest provider data, then review and adjust your pricing and fulfillment.")}</small>
              </span>
            </label>
          </fieldset>
        )}

        {errorMsg && <p className="resync-modal__error" role="alert">{errorMsg}</p>}
        {phase === "working" && <p className="resync-modal__working" role="status">{elakayAutoText("h_resync_working", "Synchronizing…")}</p>}

        {phase === "done" && (
          <>
            <p className="resync-modal__success" role="status">{ct("resyncOk")}</p>
            <SyncReport report={result?.report} ct={ct} />
          </>
        )}

        {["manual", "done"].includes(phase) && <section className="resync-modal__section resync-modal__payment">
          <h3>{elakayAutoText("h_ac9f15211ca281", "Payment acceptance")}</h3>
          <PaymentAcceptanceChoice rules={paymentRules || {}} mode={acceptanceMode} needsConfirmation={paymentNeedsConfirmation} saving={paymentSaving} onChange={saveAcceptanceMode} />
        </section>}

        {phase === "manual" && product && (
          <div className="resync-modal__manual">
            <SyncReport report={result?.report} ct={ct} />

            <section className="resync-modal__section">
              <h3>{elakayAutoText("h_resync_fulfillment", "Fulfillment")}</h3>
              <FulfillmentChanger linkId={link._id} token={token} current={ct(MODE_KEY[link.fulfillmentMode] || "modeDs")} onChanged={() => onSynced?.(link._id)} />
            </section>
            <section className="resync-modal__section">
              <h3>{elakayAutoText("h_resync_pricing", "Pricing & Desired Profit")}</h3>
              <ProductStatusSwitch
                productId={link.sellerProductId}
                token={token}
                product={product}
                onChanged={(updated) => { setProduct(updated); onSynced?.(link._id); }}
              />
            </section>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {phase === "choose" && (
          <>
            <button type="button" className="resync-modal__btn resync-modal__btn--ghost" onClick={onHide}>{elakayAutoText("h_resync_cancel", "Cancel")}</button>
            <button type="button" className="resync-modal__btn resync-modal__btn--primary" disabled={syncing} onClick={handleContinue}>{elakayAutoText("h_resync_continue", "Continue")}</button>
          </>
        )}
        {(phase === "done" || phase === "manual") && (
          <button type="button" className="resync-modal__btn resync-modal__btn--primary" onClick={onHide}>{elakayAutoText("h_resync_done", "Done")}</button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
